import { getCurrentWindow, Window as TauriWindow } from '@tauri-apps/api/window';

import { pluginCoreLogger } from '../config';

// Canonical home is the dependency-free `./window-labels`; imported for local
// use and re-exported here so `@/tauri/window` consumers (e.g. `ipc.ts`) keep a
// single import site.
import { MAIN_WINDOW_LABEL } from './window-labels';

export { MAIN_WINDOW_LABEL };

/** {@link surfaceTauriWindow} for callers that do not act on the outcome. */
export async function focusTauriWindow(): Promise<void> {
  await surfaceTauriWindow();
}

/**
 * Show, unminimize, and focus the current window, reporting whether a native
 * dialog opened afterwards will be visible to the user.
 */
export async function surfaceTauriWindow(): Promise<boolean> {
  try {
    // Tauri APIs are unavailable in the web runtime; load them only when this
    // desktop-only action is invoked.
    const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
    const window = getCurrentWebviewWindow();
    if (!window) return false;
    await window.show();
    await window.unminimize();
    await window.setFocus();
    return true;
  } catch (error) {
    pluginCoreLogger().error('ERROR - surfaceTauriWindow - error', error);
    return false;
  }
}

/**
 * Show, unminimize, and focus another Tauri window by its label. No-op when
 * already on the target window or the window can't be found. Unlike
 * {@link focusTauriWindow} (which focuses the *current* webview), this targets a
 * specific labelled window from another webview — e.g. the floating dictation
 * widget / overlay bringing the main Heidi window to the front.
 *
 * Errors are intentionally propagated so callers can attach context-specific
 * logging; keep this helper free of swallowing/logging.
 */
export async function focusTauriWindowByLabel(label: string): Promise<void> {
  if (getCurrentWindow().label === label) return;
  const target = await TauriWindow.getByLabel(label);
  if (!target) return;
  await target.show();
  await target.unminimize();
  await target.setFocus();
}
